/* Batch #14.143 — BailWorkflowPage : éditeur unifié (admin + portail).
 *
 * Une seule page utilise les endpoints /api/bail-workflow/* avec le token
 * admin OU portail selon le contexte (prop `scope`).
 *
 * Fonctions :
 *   - Liste les brouillons (à gauche)
 *   - Crée/édite un brouillon (à droite : form généré à partir des 162 champs)
 *   - Signataires (1-2 locataires + 1-2 propriétaires)
 *   - 3 boutons : Aperçu PDF · Télécharger PDF (gratuit) · Envoyer pour signature
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "@/lib/toast";
import { FileText, Plus, Trash2, Eye, Download, Save, ChevronLeft, ChevronRight, UserPlus, X, Sparkles, CreditCard, Wand2, ArrowLeft } from "lucide-react";
import { API } from "@/api";
import { getPortalToken } from "@/lib/portalSession";


const PAGE_LABELS = {
  1: "Page 1 — Identification (A·B·C)",
  2: "Page 2 — Loyer & Services (D·E)",
  3: "Page 3 — Immeuble & Signatures (F·G·H)",
};
const SECTION_LABELS = {
  section_a: "A — Parties (Locateur, Locataire + co-signataires)",
  section_b: "B — Logement",
  section_c: "C — Durée du bail",
  section_d: "D — Loyer",
  section_e: "E — Services & Conditions",
  section_f: "F — Restrictions au droit",
  section_g: "G — Loyer antérieur",
  section_h: "H — Signatures",
};

export default function BailWorkflowPage({ scope = "admin" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Batch #14.213 — Filtre par master_id quand admin arrive depuis Gestion immeuble
  const masterId = searchParams.get("master_id") || "";
  const token = useMemo(
    () => (scope === "portal"
      ? getPortalToken()
      : localStorage.getItem("token")),
    [scope]
  );
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Batch #14.149 — Retour selon scope
  const backUrl = scope === "portal"
    ? "/portal/dashboard"
    : (masterId ? `/admin/immeubles?master_id=${masterId}` : "/admin/immeubles");
  const backLabel = scope === "portal" ? "Retour au portail" : "Retour à Gestion immeuble";

  const [schema, setSchema] = useState({ by_page: {}, fields: [] });
  const [drafts, setDrafts] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [values, setValues] = useState({});
  const [signers, setSigners] = useState([]);
  const [customInitials, setCustomInitials] = useState({ locateur: "", locataire: "" });
  const [title, setTitle] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [busy, setBusy] = useState(false);
  const dirtyRef = useRef(false);

  const loadAll = useCallback(async () => {
    try {
      // Batch #14.213 — Si master_id fourni (admin depuis Hub Immeuble), filtre la liste
      const draftsUrl = masterId
        ? `${API}/bail-workflow/drafts?master_id=${encodeURIComponent(masterId)}`
        : `${API}/bail-workflow/drafts`;
      const [s, d, p] = await Promise.all([
        axios.get(`${API}/bail-workflow/template-fields`, { headers: authHeader }),
        axios.get(draftsUrl, { headers: authHeader }),
        axios.get(`${API}/bail-workflow/pricing`, { headers: authHeader }),
      ]);
      setSchema(s.data);
      setDrafts(d.data.items || []);
      setPricing(p.data);
    } catch (e) {
      toast.error("Erreur de chargement : " + (e.response?.data?.detail || e.message));
    }
  }, [authHeader, masterId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Batch #14.163 — HOTFIX critique : Stripe success handler.
  // Quand Stripe redirige vers /bail-workflow/payment-success?draft_id=X&session_id=Y,
  // on déclenche AUTOMATIQUEMENT send-for-signature avec le session_id du paiement.
  // SANS ce handler, le client paie mais aucun email n'est envoyé aux signataires.
  useEffect(() => {
    const url = new URL(window.location.href);
    const isPaymentSuccess = url.pathname.includes("/bail-workflow/payment-success")
      || url.pathname.includes("/payment-success");
    if (!isPaymentSuccess) return;
    const draftId = url.searchParams.get("draft_id");
    const sessionId = url.searchParams.get("session_id");
    if (!draftId || !sessionId) {
      toast.error("Paramètres de paiement manquants. Contactez le support.");
      return;
    }
    (async () => {
      try {
        toast.loading("Paiement confirmé — envoi du bail en cours…", { id: "bail-pay-process" });
        const r = await axios.post(
          `${API}/bail-workflow/drafts/${draftId}/send-for-signature`,
          { origin_url: window.location.origin, payment_session_id: sessionId },
          { headers: authHeader }
        );
        toast.success(
          `✓ Bail envoyé pour signature !\nPremier signataire : ${r.data.first_signer?.name}`,
          { id: "bail-pay-process", duration: 8000 }
        );
        // Nettoie l'URL et recharge la liste pour faire apparaître le brouillon en statut "sent"
        const cleanUrl = scope === "portal" ? "/portal/bail-workflow" : "/admin/bail-workflow";
        window.history.replaceState({}, "", cleanUrl);
        await loadAll();
      } catch (e) {
        toast.error(
          `Paiement reçu mais envoi du bail en erreur : ${e.response?.data?.detail || e.message}. Contactez dgfiscalflow@dgcomptabilite.ca avec votre numéro de session : ${sessionId}`,
          { id: "bail-pay-process", duration: 15000 }
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectDraft = (draft) => {
    setSelectedDraft(draft);
    setValues(draft.values || {});
    setSigners(draft.signers || []);
    setCustomInitials(draft.custom_initials || { locateur: "", locataire: "" });
    setTitle(draft.title || "");
    setActivePage(1);
    dirtyRef.current = false;
  };

  const onNewDraft = async () => {
    setBusy(true);
    try {
      // Batch #14.213 — Si master_id est dans l'URL, on l'inclut dans le draft
      // pour qu'il apparaisse dans le Suivi signatures du master correspondant.
      const body = { title: `Nouveau bail — ${new Date().toLocaleDateString("fr-CA")}`, values: {}, signers: [] };
      if (masterId) body.master_id = masterId;
      const r = await axios.post(
        `${API}/bail-workflow/drafts`,
        body,
        { headers: authHeader }
      );
      await loadAll();
      onSelectDraft(r.data);
      toast.success("Brouillon créé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de création");
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    if (!selectedDraft) return;
    setBusy(true);
    try {
      const r = await axios.put(
        `${API}/bail-workflow/drafts/${selectedDraft.id}`,
        { title, values, signers, custom_initials: customInitials },
        { headers: authHeader }
      );
      setSelectedDraft(r.data);
      dirtyRef.current = false;
      toast.success("Brouillon enregistré");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!selectedDraft) return;
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      await axios.delete(`${API}/bail-workflow/drafts/${selectedDraft.id}`, { headers: authHeader });
      toast.success("Brouillon supprimé");
      setSelectedDraft(null);
      setValues({});
      setSigners([]);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de suppression");
    }
  };

  const onPreview = async () => {
    if (!selectedDraft) return;
    if (dirtyRef.current && !window.confirm("Modifications non sauvegardées. Continuer ?")) return;
    try {
      const r = await axios.post(
        `${API}/bail-workflow/drafts/${selectedDraft.id}/preview-pdf`,
        {},
        { headers: authHeader, responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Erreur de prévisualisation");
    }
  };

  const onDownload = async () => {
    if (!selectedDraft) return;
    if (dirtyRef.current) await onSave();
    try {
      const r = await axios.post(
        `${API}/bail-workflow/drafts/${selectedDraft.id}/download`,
        {},
        { headers: authHeader, responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      // Batch #14.161 — Le download retourne désormais le template VIERGE
      a.download = `bail-tal-vierge-${selectedDraft.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(a.href);
      a.remove();
      toast.success("PDF vierge téléchargé (à imprimer et remplir manuellement)");
    } catch (e) {
      toast.error("Erreur de téléchargement");
    }
  };

  const onSendForSignature = async () => {
    if (!selectedDraft) return;
    if (signers.filter((s) => s.role === "tenant").length === 0) {
      toast.error("Ajoutez au moins un locataire");
      return;
    }
    if (signers.filter((s) => s.role === "owner").length === 0) {
      toast.error("Ajoutez au moins un propriétaire");
      return;
    }
    if (dirtyRef.current) await onSave();

    // Admin = illimité, pas de Stripe Checkout. Portail = quota + Stripe si dépassé.
    if (!pricing?.unlimited) {
      const free = pricing?.free_remaining ?? 0;
      if (free <= 0) {
        const ok = window.confirm(
          `Vous avez utilisé votre envoi gratuit. Le prochain coûtera ${pricing?.amount_total || 10} $ CAD.\n\nProcéder au paiement Stripe ?`
        );
        if (!ok) return;
        try {
          // Batch #14.163 — Passe explicitement return_path pour que Stripe
          // redirige sur la BONNE URL (sinon /bail-workflow/payment-success
          // n'existe pas dans le router React et le client perd son paiement).
          const returnPath = scope === "portal"
            ? "/portal/bail-workflow/payment-success"
            : "/admin/bail-workflow/payment-success";
          const r = await axios.post(
            `${API}/bail-workflow/drafts/${selectedDraft.id}/create-checkout`,
            { origin_url: window.location.origin, return_path: returnPath },
            { headers: authHeader }
          );
          if (r.data.checkout_url) {
            window.location.href = r.data.checkout_url;
            return;
          }
        } catch (e) {
          toast.error("Erreur Stripe : " + (e.response?.data?.detail || e.message));
          return;
        }
      }
    }

    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/bail-workflow/drafts/${selectedDraft.id}/send-for-signature`,
        { origin_url: window.location.origin },
        { headers: authHeader }
      );
      toast.success(
        `Bail envoyé pour signature !\nPremier signataire : ${r.data.first_signer?.name}\n${r.data.free_used ? "(envoi gratuit utilisé)" : "(envoi payant)"}`,
        { duration: 6000 }
      );
      await loadAll();
      onSelectDraft({ ...selectedDraft, status: "sent" });
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Paiement requis. Veuillez recharger la page.");
      } else {
        toast.error(e.response?.data?.detail || "Erreur d'envoi");
      }
    } finally {
      setBusy(false);
    }
  };

  // Handlers de champs
  const updateValue = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    dirtyRef.current = true;
  };

  const addSigner = (role) => {
    const existing = signers.filter((s) => s.role === role);
    if (existing.length >= 2) {
      toast.error(`Maximum 2 ${role === "tenant" ? "locataires" : "propriétaires"}`);
      return;
    }
    setSigners([...signers, { role, name: "", email: "", phone: "" }]);
    dirtyRef.current = true;
  };

  const updateSigner = (idx, patch) => {
    setSigners((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    dirtyRef.current = true;
  };

  const removeSigner = (idx) => {
    setSigners((prev) => prev.filter((_, i) => i !== idx));
    dirtyRef.current = true;
  };

  // Batch #14.147 — Auto-fill co-signataires (Section A2) depuis la liste des signers
  const autofillCoSigners = () => {
    const tenants = signers.filter((s) => s.role === "tenant");
    const owners = signers.filter((s) => s.role === "owner");
    const updates = {};
    if (tenants[1]?.name) updates.locataire_2_nom = tenants[1].name;
    if (tenants[1]?.email) updates.locataire_2_courriel = tenants[1].email;
    if (tenants[1]?.phone) updates.locataire_2_tel_cell = tenants[1].phone;
    if (owners[1]?.name) updates.locateur_2_nom = owners[1].name;
    if (owners[1]?.email) updates.locateur_2_courriel = owners[1].email;
    if (owners[1]?.phone) updates.locateur_2_tel_cell = owners[1].phone;
    // Aussi : pré-remplir locataire_nom et locateur_nom si vides
    if (tenants[0]?.name && !values.locataire_nom) updates.locataire_nom = tenants[0].name;
    if (tenants[0]?.email && !values.locataire_courriel) updates.locataire_courriel = tenants[0].email;
    if (owners[0]?.name && !values.locateur_nom) updates.locateur_nom = owners[0].name;
    if (owners[0]?.email && !values.locateur_courriel) updates.locateur_courriel = owners[0].email;
    const filledCount = Object.keys(updates).length;
    if (filledCount === 0) {
      toast.message("Aucun co-signataire à recopier", { description: "Ajoutez d'abord un 2e locataire ou propriétaire dans la liste des signataires." });
      return;
    }
    setValues((prev) => ({ ...prev, ...updates }));
    dirtyRef.current = true;
    toast.success(`${filledCount} champs auto-remplis depuis les signataires`);
  };

  if (!schema.fields.length) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="bail-workflow-loading">
        Chargement du formulaire bail TAL…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="bail-workflow-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(backUrl)}
            className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 flex items-center justify-center text-gray-600 hover:text-indigo-700 transition shadow-sm"
            data-testid="bail-back-btn"
            title={backLabel}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bail TAL — Nouveau workflow</h1>
            <p className="text-sm text-gray-500">
              Remplissez vous-même le formulaire officiel, téléchargez ou envoyez pour signature électronique
            </p>
          </div>
        </div>
        {pricing && (
          <div className="text-right">
            <div className="text-xs text-gray-500">Envois pour signature</div>
            <div className="text-sm font-medium">
              {pricing.unlimited ? (
                <span className="text-emerald-700">∞ Illimité gratuit (admin)</span>
              ) : (
                <>
                  <span className="text-emerald-700">{pricing.free_remaining} gratuit{pricing.free_remaining > 1 ? "s" : ""}</span>
                  {" · "}
                  <span className="text-gray-700">{pricing.amount_total} $ ensuite</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar : liste brouillons */}
        <div className="col-span-12 md:col-span-3 bg-white rounded-xl border border-gray-200 p-3">
          <button
            onClick={onNewDraft}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            data-testid="bail-new-draft-btn"
          >
            <Plus className="w-4 h-4" /> Nouveau bail
          </button>
          <div className="space-y-1 max-h-[60vh] overflow-y-auto">
            {drafts.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-6">Aucun brouillon</div>
            )}
            {drafts.map((d) => (
              <button
                key={d.id}
                onClick={() => onSelectDraft(d)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                  selectedDraft?.id === d.id
                    ? "bg-indigo-50 border border-indigo-200 text-indigo-900"
                    : "hover:bg-gray-50 border border-transparent"
                }`}
                data-testid={`bail-draft-item-${d.id}`}
              >
                <div className="font-medium truncate">{d.title}</div>
                <div className="text-xs text-gray-500 flex items-center justify-between mt-0.5">
                  <span>{new Date(d.updated_at).toLocaleDateString("fr-CA")}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    d.status === "sent" ? "bg-amber-100 text-amber-700" :
                    d.status === "signed" ? "bg-emerald-100 text-emerald-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>
                    {d.status === "sent" ? "envoyé" : d.status === "signed" ? "signé" : "brouillon"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main : éditeur */}
        <div className="col-span-12 md:col-span-9">
          {!selectedDraft ? (
            <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Sélectionnez un brouillon ou créez-en un nouveau</p>
            </div>
          ) : (
            <BailEditor
              schema={schema}
              draft={selectedDraft}
              title={title}
              setTitle={(v) => { setTitle(v); dirtyRef.current = true; }}
              values={values}
              updateValue={updateValue}
              signers={signers}
              addSigner={addSigner}
              updateSigner={updateSigner}
              removeSigner={removeSigner}
              autofillCoSigners={autofillCoSigners}
              customInitials={customInitials}
              updateCustomInitials={(p) => { setCustomInitials((prev) => ({ ...prev, ...p })); dirtyRef.current = true; }}
              activePage={activePage}
              setActivePage={setActivePage}
              busy={busy}
              onSave={onSave}
              onPreview={onPreview}
              onDownload={onDownload}
              onSendForSignature={onSendForSignature}
              onDelete={onDelete}
              pricing={pricing}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function BailEditor({
  schema, draft, title, setTitle, values, updateValue, signers, addSigner,
  updateSigner, removeSigner, autofillCoSigners, customInitials, updateCustomInitials,
  activePage, setActivePage, busy,
  onSave, onPreview, onDownload, onSendForSignature, onDelete, pricing,
}) {
  const pages = [1, 2, 3];
  const pageSchema = schema.by_page[activePage] || schema.by_page[String(activePage)] || {};
  const isLocked = draft.status === "sent" || draft.status === "signed";

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-200">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre du bail"
          disabled={isLocked}
          className="w-full px-3 py-2 mb-3 border border-gray-200 rounded-lg text-base font-medium disabled:bg-gray-50"
          data-testid="bail-title-input"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={onSave} disabled={busy || isLocked}
                  className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="bail-save-btn">
            <Save className="w-4 h-4" /> Enregistrer
          </button>
          <button onClick={onPreview} disabled={busy}
                  className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="bail-preview-btn"
                  title="Aperçu rempli avec filigrane — pour vérifier vos saisies (non imprimable, non téléchargeable)">
            <Eye className="w-4 h-4" /> Aperçu PDF
          </button>
          <button onClick={onDownload} disabled={busy}
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="bail-download-btn"
                  title="Téléchargement gratuit du PDF VIERGE pour impression et signature manuelle">
            <Download className="w-4 h-4" /> Télécharger vierge (gratuit)
          </button>
          <button onClick={onSendForSignature} disabled={busy || isLocked}
                  className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="bail-send-signature-btn">
            {(pricing?.unlimited || pricing?.free_remaining > 0) ? <Sparkles className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
            Envoyer pour signature {
              pricing?.unlimited ? "(illimité)"
              : pricing?.free_remaining > 0 ? "(gratuit)"
              : `(${pricing?.amount_total || 10} $)`
            }
          </button>
          <div className="flex-1" />
          {!isLocked && (
            <button onClick={onDelete}
                    className="px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50 text-sm inline-flex items-center gap-1.5"
                    data-testid="bail-delete-btn">
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          )}
        </div>
        {isLocked && (
          <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            🔒 Ce bail est verrouillé (statut : <strong>{draft.status}</strong>). Pour modifier, créez un nouveau brouillon.
          </div>
        )}
      </div>

      {/* Signataires */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700">Signataires (locataires d&apos;abord, puis propriétaires)</h3>
          <button
            onClick={autofillCoSigners}
            disabled={isLocked || signers.length === 0}
            className="text-[10px] font-semibold px-2 py-1 rounded bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="bail-autofill-cosigners-btn"
            title="Copie automatiquement les noms/emails des signataires dans les champs Section A et A2"
          >
            <Wand2 className="w-3 h-3" /> Auto-remplir Section A
          </button>
        </div>
        <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 mb-3">
          ✅ <strong>2 personnes ?</strong> Remplissez les infos du <em>2e locataire</em> et/ou <em>2e propriétaire</em> dans
          la <strong>Section A</strong> (Page 1). Chaque personne a son <strong>propre bloc</strong> sur le bail TAL (pas de fusion).
          Cliquez « Auto-remplir Section A » pour copier les noms/emails depuis la liste des signataires.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {["tenant", "owner"].map((role) => {
            const list = signers.map((s, i) => ({ ...s, idx: i })).filter((s) => s.role === role);
            return (
              <div key={role} className={`rounded-lg p-3 border ${role === "tenant" ? "border-blue-200 bg-blue-50/40" : "border-emerald-200 bg-emerald-50/40"}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    {role === "tenant" ? "🧑 Locataires" : "🏠 Propriétaires"} ({list.length}/2)
                  </span>
                  <button onClick={() => addSigner(role)} disabled={list.length >= 2 || isLocked}
                          className="text-xs px-2 py-1 rounded bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1"
                          data-testid={`bail-add-signer-${role}`}>
                    <UserPlus className="w-3 h-3" /> Ajouter
                  </button>
                </div>
                {list.length === 0 ? (
                  <div className="text-xs text-gray-400 italic">Aucun {role === "tenant" ? "locataire" : "propriétaire"}</div>
                ) : (
                  <div className="space-y-2">
                    {list.map((s) => (
                      <div key={s.idx} className="bg-white rounded p-2 border border-gray-100">
                        <div className="flex items-center gap-2">
                          <input type="text" placeholder="Nom complet" value={s.name}
                                 disabled={isLocked}
                                 onChange={(e) => updateSigner(s.idx, { name: e.target.value })}
                                 className="flex-1 px-2 py-1 text-sm border-b border-gray-200 focus:outline-none focus:border-indigo-400"
                                 data-testid={`bail-signer-name-${s.idx}`} />
                          <button onClick={() => removeSigner(s.idx)} disabled={isLocked}
                                  className="text-rose-400 hover:text-rose-600 disabled:opacity-30">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <input type="email" placeholder="Courriel" value={s.email}
                               disabled={isLocked}
                               onChange={(e) => updateSigner(s.idx, { email: e.target.value })}
                               className="w-full px-2 py-1 mt-1 text-xs border-b border-gray-200 focus:outline-none focus:border-indigo-400"
                               data-testid={`bail-signer-email-${s.idx}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Batch #14.148 — Initiales custom (override auto-générées) */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 bg-violet-50 border border-violet-200 rounded-lg p-3">
          <div className="sm:col-span-2 text-[11px] text-violet-800 font-semibold">
            🖊️ Initiales (footer de chaque page) — auto-calculées depuis les noms, modifiable si nécessaire
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-violet-700 mb-0.5">
              Initiales locateur
            </label>
            <input
              type="text"
              maxLength={6}
              placeholder="Auto : JD/ML"
              value={customInitials.locateur || ""}
              disabled={isLocked}
              onChange={(e) => updateCustomInitials({ locateur: e.target.value.toUpperCase() })}
              className="w-full px-2 py-1 text-sm border border-violet-200 rounded font-mono uppercase tracking-wider disabled:bg-gray-50"
              data-testid="bail-custom-initials-locateur"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase font-semibold text-violet-700 mb-0.5">
              Initiales locataire
            </label>
            <input
              type="text"
              maxLength={6}
              placeholder="Auto : AT/BB"
              value={customInitials.locataire || ""}
              disabled={isLocked}
              onChange={(e) => updateCustomInitials({ locataire: e.target.value.toUpperCase() })}
              className="w-full px-2 py-1 text-sm border border-violet-200 rounded font-mono uppercase tracking-wider disabled:bg-gray-50"
              data-testid="bail-custom-initials-locataire"
            />
          </div>
        </div>
      </div>

      {/* Onglets de page */}
      <div className="border-b border-gray-200 px-4 pt-3">
        <div className="flex items-center gap-1">
          {pages.map((p) => (
            <button key={p} onClick={() => setActivePage(p)}
                    className={`px-3 py-2 text-sm rounded-t-lg border-b-2 transition ${
                      activePage === p
                        ? "border-indigo-600 text-indigo-700 bg-indigo-50/50"
                        : "border-transparent text-gray-500 hover:text-gray-800"
                    }`}
                    data-testid={`bail-page-tab-${p}`}>
              {PAGE_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {/* Form fields */}
      <div className="p-4 max-h-[60vh] overflow-y-auto">
        {Object.keys(pageSchema).sort().map((sectionKey) => (
          <FieldSection
            key={sectionKey}
            sectionKey={sectionKey}
            label={SECTION_LABELS[sectionKey] || sectionKey}
            fields={pageSchema[sectionKey] || []}
            values={values}
            updateValue={updateValue}
            disabled={isLocked}
          />
        ))}
      </div>

      {/* Pagination */}
      <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setActivePage((p) => Math.max(1, p - 1))} disabled={activePage === 1}
                className="px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100 disabled:opacity-30 inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Précédente
        </button>
        <span className="text-xs text-gray-500">Page {activePage} sur 3</span>
        <button onClick={() => setActivePage((p) => Math.min(3, p + 1))} disabled={activePage === 3}
                className="px-3 py-1.5 text-sm rounded-lg hover:bg-gray-100 disabled:opacity-30 inline-flex items-center gap-1">
          Suivante <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function FieldSection({ sectionKey, label, fields, values, updateValue, disabled }) {
  if (!fields.length) return null;
  const textFields = fields.filter((f) => f.type === "text");
  const checkboxFields = fields.filter((f) => f.type === "checkbox");

  return (
    <div className="mb-5" data-testid={`bail-section-${sectionKey}`}>
      <h4 className="text-sm font-bold text-gray-800 mb-2 pb-1 border-b border-gray-100">{label}</h4>
      {textFields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
          {textFields.map((f) => (
            <div key={f.key}>
              <label className="block text-[11px] text-gray-500 mb-0.5">{f.label}</label>
              <input
                type="text"
                value={values[f.key] || ""}
                disabled={disabled}
                onChange={(e) => updateValue(f.key, e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-indigo-400 disabled:bg-gray-50"
                data-testid={`bail-field-${f.key}`}
              />
            </div>
          ))}
        </div>
      )}
      {checkboxFields.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
          {checkboxFields.map((f) => (
            <label key={f.key}
                   className="inline-flex items-center gap-2 px-2 py-1 text-xs hover:bg-gray-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={!!values[f.key]}
                disabled={disabled}
                onChange={(e) => updateValue(f.key, e.target.checked)}
                className="rounded text-indigo-600 focus:ring-indigo-500"
                data-testid={`bail-field-${f.key}`}
              />
              <span className="text-gray-700">{f.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
